import {
  CircleAlertIcon,
  CheckIcon,
  MapPinnedIcon,
  PackageIcon,
  ShoppingBagIcon,
  TruckIcon,
  type LucideIcon,
  ZapIcon,
} from 'lucide-react';
import { Fragment, useMemo } from 'react';

import type { OrderDTO } from '@invenco/sales-interface/interfaces';
import type { ShipmentDTO } from '@invenco/shipping-interface';

import { StatusBadge } from '@/components/status-badge';
import {
  Timeline,
  TimelineEntry,
  TimelineEntryBody,
  TimelineEntryHeader,
  TimelineEntryMeta,
  TimelineEntryTitle,
  TimelineSectionMarker,
  TimelineSkeletonEntry,
} from '@/components/timeline';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { formatDateTime } from '@/shared/helpers';

import { buildOrderTimeline, type OrderTimelineCategory } from './build-order-timeline';

type Props = {
  order?: OrderDTO;
  shipments?: ShipmentDTO[];
  isLoading?: boolean;
};

const categoryIcon: Record<OrderTimelineCategory, LucideIcon> = {
  order: ShoppingBagIcon,
  address: MapPinnedIcon,
  inventory: PackageIcon,
  automation: ZapIcon,
  fulfillment: PackageIcon,
  shipment: TruckIcon,
};

export function OrderTimeline({ order, shipments, isLoading }: Props) {
  const sections = useMemo(() => buildOrderTimeline({ order, shipments }), [order, shipments]);

  if (isLoading) {
    return (
      <Timeline aria-label="Loading timeline">
        <TimelineSkeletonEntry />
        <TimelineSkeletonEntry />
        <TimelineSkeletonEntry />
      </Timeline>
    );
  }

  if (sections.length === 0) {
    return <p className="text-muted-foreground">{EMPTY_TEXT_VALUE}</p>;
  }

  return (
    <Timeline aria-label="Order timeline">
      {sections.map((section) => (
        <Fragment key={section.marker}>
          <TimelineSectionMarker>{section.marker}</TimelineSectionMarker>
          {section.entries.map((entry) => {
            const Icon =
              entry.section === 'COMPLETED'
                ? CheckIcon
                : entry.type === 'alert'
                  ? CircleAlertIcon
                  : categoryIcon[entry.category];

            return (
              <TimelineEntry key={entry.id} icon={Icon} type={entry.type}>
                <TimelineEntryHeader>
                  <TimelineEntryTitle>{entry.title}</TimelineEntryTitle>
                  <TimelineEntryMeta>
                    {entry.section === 'IN-PROGRESS' && entry.type === 'progress' ? (
                      <StatusBadge type="progress">In progress</StatusBadge>
                    ) : (
                      formatDateTime(entry.timestamp)
                    )}
                  </TimelineEntryMeta>
                </TimelineEntryHeader>
                {entry.description ? (
                  <TimelineEntryBody>{entry.description}</TimelineEntryBody>
                ) : null}
              </TimelineEntry>
            );
          })}
        </Fragment>
      ))}
    </Timeline>
  );
}
